import { Head } from "vite-react-ssg";
import { Eyebrow } from "@/components/Eyebrow";
import { Button } from "@/components/Button";
import {
  Section,
  SectionHeading,
  Stat,
  CodeCard,
  Pill,
} from "@/components/kit";
import { cn } from "@/lib/cn";
import { LoopDiagram } from "@/components/LoopDiagram";
import { HeroBackdrop } from "@/components/HeroBackdrop";
import productHero from "@/assets/hero/product-hero.jpg";
import {
  PRODUCT_HERO,
  PILLAR_CANVAS,
  PILLAR_NOTEBOOK,
  PILLAR_3D,
  PILLAR_VIZ,
  PILLAR_CODEGEN,
  PILLAR_GENERATIVE,
  PILLAR_OPTIM,
  PILLAR_COMPAT,
  PILLAR_COLLAB,
  PRODUCT_CTA,
} from "@/content/product";
import canvasShot from "@/assets/shots/canvas-sir.png";
import mugShot from "@/assets/shots/the3djin-mug.png";
import widgetShot from "@/assets/shots/param-widget.png";
import vizEvEffMap from "@/assets/shots/viz-ev-effmap.png";
import vizUr5Arm from "@/assets/shots/viz-ur5-arm.png";
import vizRocketDispersion from "@/assets/shots/viz-rocket-dispersion.png";
import notebookOutput from "@/assets/shots/notebook-output.png";

/** Framed real product screenshot. */
function Shot({ src, alt }: { src: string; alt: string }) {
  return (
    <figure className="overflow-hidden rounded-2xl border border-border bg-card shadow-lift">
      <img src={src} alt={alt} className="block w-full" loading="lazy" />
    </figure>
  );
}

// ---------------------------------------------------------------------------
// Small shared helpers (inline — no new files needed)
// ---------------------------------------------------------------------------

function BulletList({ items }: { items: string[] }) {
  return (
    <ul className="mt-5 space-y-2">
      {items.map((item) => (
        <li key={item} className="flex items-start gap-2.5 text-sm leading-relaxed text-muted-foreground">
          <span className="mt-0.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

/** Left-text / right-visual alternating two-column pillar layout. */
function PillarSection({
  flip = false,
  wash = "plain",
  bordered = false,
  children,
}: {
  flip?: boolean;
  wash?: "plain" | "surface" | "wash";
  bordered?: boolean;
  children: [React.ReactNode, React.ReactNode]; // [text, visual]
}) {
  const [textPane, visualPane] = children;
  return (
    <Section wash={wash} bordered={bordered}>
      <div className="grid items-center gap-12 md:grid-cols-2">
        <div className={cn(flip && "md:order-2")}>{textPane}</div>
        <div className={cn(flip && "md:order-1")}>{visualPane}</div>
      </div>
    </Section>
  );
}

/** A single "text pane" inside a pillar. */
function TextPane({
  eyebrow,
  title,
  body,
  children,
}: {
  eyebrow: string;
  title: string;
  body: string;
  children?: React.ReactNode;
}) {
  return (
    <div>
      <Eyebrow>{eyebrow}</Eyebrow>
      <h2 className="mt-4 text-3xl font-extrabold tracking-tight sm:text-4xl">{title}</h2>
      <p className="mt-4 max-w-md text-lg leading-relaxed text-muted-foreground">{body}</p>
      {children}
    </div>
  );
}

/** A centered umbrella band that introduces one of the three pillar groups. */
function GroupHeader({
  eyebrow,
  title,
  intro,
}: {
  eyebrow: string;
  title: string;
  intro: string;
}) {
  return (
    <Section wash="wash" bordered>
      <SectionHeading eyebrow={eyebrow} title={title} intro={intro} />
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Visual panels
// ---------------------------------------------------------------------------

/** Dotted-grid panel wrapping any visual content. */
function MockPanel({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={cn("bg-dots overflow-hidden rounded-2xl border border-border bg-surface p-6 shadow-soft", className)}>
      {children}
    </div>
  );
}

/** Reusable terminal-chrome header bar for mock panels. */
function TermBar({ label }: { label: string }) {
  return (
    <div className="mb-3 flex items-center gap-1.5">
      <span className="h-2.5 w-2.5 rounded-full bg-signal/60" />
      <span className="h-2.5 w-2.5 rounded-full bg-accent" />
      <span className="h-2.5 w-2.5 rounded-full bg-primary" />
      <span className="ml-2 font-mono text-xs text-muted-foreground">{label}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Canvas — mock canvas panel
// ---------------------------------------------------------------------------
function CanvasMock() {
  return (
    <div className="space-y-3">
      <Shot
        src={canvasShot}
        alt="A DjiniousLab canvas: an SIR epidemic model wired to three live scope plots"
      />
      <div className="flex flex-wrap gap-2">
        {["Tsit5", "Rodas5P", "IDA/DAE", ">1M s/s Arrow"].map((t) => (
          <Pill key={t} tone="primary">{t}</Pill>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Notebook — code card panel
// ---------------------------------------------------------------------------
function NotebookMock() {
  return (
    <div className="space-y-3">
      <CodeCard
        title={PILLAR_NOTEBOOK.codeTitle}
        lang={PILLAR_NOTEBOOK.codeLang}
        code={PILLAR_NOTEBOOK.code}
      />
      <Shot
        src={notebookOutput}
        alt="A DjiniousLab notebook cell rendering an interactive Plotly figure inline — ten colour-coded Gaussian clusters — as the cell finishes running"
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// 3D CAD — code card
// ---------------------------------------------------------------------------
function CadMock() {
  return (
    <div className="space-y-3">
      <CodeCard
        title={PILLAR_3D.codeTitle}
        lang={PILLAR_3D.codeLang}
        code={PILLAR_3D.code}
      />
      <div className="grid grid-cols-2 gap-3">
        <Shot src={mugShot} alt="A parametric mug rendered in 3D from the3djin in a notebook cell" />
        <Shot src={widgetShot} alt="A the3djin ParameterSet rendered as a widget table with sliders, ranges and units" />
      </div>
      <div className="flex flex-wrap gap-2">
        {["show(part) → 3D", "show(sketch) → SVG", "show(ps) → widgets"].map((t) => (
          <Pill key={t} tone="accent">{t}</Pill>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Visualization — real renderer outputs captured from the running app
// ---------------------------------------------------------------------------
function VizMock() {
  return (
    <div className="space-y-3">
      <Shot
        src={vizEvEffMap}
        alt="A Plotly efficiency map of a PMSM traction motor (torque × speed → η) with the peak-efficiency envelope, from the Full EV Vehicle program, rendered inline in a DjiniousLab notebook cell"
      />
      <Shot
        src={vizUr5Arm}
        alt="A Plotly 3-D render of the UR5 six-axis manipulator — joints, end-effector and base — from the UR5 Arm program, rendered inline from a notebook cell, drag-to-orbit"
      />
      <Shot
        src={vizRocketDispersion}
        alt="A Plotly Monte-Carlo touchdown-dispersion scatter with CEP / R95 rings, from the Reusable Rocket program, rendered inline from a notebook cell"
      />
      {/* The full renderer family — every magic-prefix output type. */}
      <div className="flex flex-wrap gap-2">
        {PILLAR_VIZ.vizTypes.map((v) => (
          <Pill key={v.label} tone={v.tone}>{v.label}</Pill>
        ))}
      </div>
      <p className="text-center font-mono text-[10px] text-muted-foreground">
        all renderers lazy-loaded · streams as the solver runs
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Code-gen — CodeCard
// ---------------------------------------------------------------------------
function CodegenMock() {
  return (
    <CodeCard
      title={PILLAR_CODEGEN.codeTitle}
      lang={PILLAR_CODEGEN.codeLang}
      code={PILLAR_CODEGEN.code}
    />
  );
}

// ---------------------------------------------------------------------------
// Generative design — the3djin shots + roadmap pills
// ---------------------------------------------------------------------------
function GenerativeMock() {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <Shot src={mugShot} alt="A parametric mug rendered in 3D from the3djin in a notebook cell" />
        <Shot src={widgetShot} alt="A the3djin ParameterSet rendered as a widget table with sliders, ranges and units" />
      </div>
      <div className="flex flex-wrap gap-2">
        <Pill tone="accent">STEP / STL export — today</Pill>
        <Pill tone="neutral">Generative design — roadmap</Pill>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Optimization — convergence table mock
// ---------------------------------------------------------------------------
function OptimMock() {
  return (
    <MockPanel>
      <TermBar label="studies · CMA-ES convergence" />
      <div className="overflow-hidden rounded-xl border border-border">
        <table className="w-full font-mono text-xs">
          <thead>
            <tr className="border-b border-border bg-surface text-muted-foreground">
              <th className="px-3 py-2 text-left">iter</th>
              <th className="px-3 py-2 text-right">loss</th>
              <th className="px-3 py-2 text-right">progress</th>
            </tr>
          </thead>
          <tbody>
            {PILLAR_OPTIM.convergenceRows.map((row) => (
              <tr key={row.iter} className="border-b border-border last:border-0">
                <td className="px-3 py-1.5 text-muted-foreground">{row.iter}</td>
                <td className="px-3 py-1.5 text-right text-primary">{row.loss}</td>
                <td className="px-3 py-1.5 text-right">
                  <div className="inline-flex w-20 overflow-hidden rounded-full bg-border">
                    <div
                      className="h-1.5 rounded-full bg-primary/70 transition-all"
                      style={{ width: `${(1 - parseFloat(row.loss)) * 100}%` }}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {["Latin Hypercube", "CMA-ES", "Bayes GP", "PPO / SAC"].map((a) => (
          <Pill key={a} tone="neutral">{a}</Pill>
        ))}
      </div>
    </MockPanel>
  );
}

// ---------------------------------------------------------------------------
// Interop — FMI / open-standards list (no vs-MATLAB comparison)
// ---------------------------------------------------------------------------
function InteropMock() {
  return (
    <MockPanel>
      <TermBar label="open standards" />
      <div className="overflow-hidden rounded-xl border border-border">
        <table className="w-full text-xs">
          <tbody>
            {PILLAR_COMPAT.interopRows.map((row) => (
              <tr key={row.label} className="border-b border-border last:border-0">
                <td className="px-3 py-2 font-semibold text-primary">{row.label}</td>
                <td className="px-3 py-2 text-muted-foreground">{row.detail}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </MockPanel>
  );
}

// ---------------------------------------------------------------------------
// Collaboration — visual panel
// ---------------------------------------------------------------------------
function CollabMock() {
  const presenceColors = ["bg-primary", "bg-accent", "bg-signal/70", "bg-primary/50"];
  return (
    <MockPanel>
      <TermBar label="motor_drive.canvas — 4 collaborators" />
      {/* Presence avatars */}
      <div className="mb-4 flex items-center gap-1.5">
        {presenceColors.map((c, i) => (
          <span
            key={i}
            className={cn(
              "flex h-7 w-7 items-center justify-center rounded-full text-[10px] font-bold text-white shadow-soft",
              c,
            )}
          >
            {String.fromCharCode(65 + i)}
          </span>
        ))}
        <span className="ml-2 text-xs text-muted-foreground">editing now</span>
      </div>
      {/* Git commit log mock */}
      <div className="space-y-1.5 font-mono text-[10px]">
        {[
          { hash: "f3a8c2d", msg: "tune damping ratio α=0.35", who: "A", ago: "just now" },
          { hash: "e91b3f1", msg: "add thermal subsystem",      who: "B", ago: "3 min ago" },
          { hash: "c7d4a09", msg: "baseline motor_drive v2",    who: "C", ago: "1 hr ago" },
        ].map((c) => (
          <div key={c.hash} className="flex items-center gap-2 text-muted-foreground">
            <code className="text-primary">{c.hash}</code>
            <span className="flex-1 truncate">{c.msg}</span>
            <span className="shrink-0">{c.ago}</span>
          </div>
        ))}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {["Yjs CRDT", "autosave → git", "GIR hash", "bit-exact replay"].map((t) => (
          <Pill key={t} tone="primary">{t}</Pill>
        ))}
      </div>
    </MockPanel>
  );
}

// ---------------------------------------------------------------------------
// Stats strip
// ---------------------------------------------------------------------------
function StatsStrip() {
  return (
    <Section wash="surface" bordered>
      <div className="grid grid-cols-2 gap-8 sm:grid-cols-4">
        <Stat value="600+" label="built-in blocks" />
        <Stat value="6"    label="solver options" />
        <Stat value=">1M"  label="samples/s via Arrow" />
        <Stat value="FMI 3.0" label="import + export" />
      </div>
    </Section>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export default function Features() {
  return (
    <>
      <Head>
        <title>Product — DjiniousLab</title>
        <meta
          name="description"
          content="A deep tour of DjiniousLab's capabilities, grouped by the three pillars of model-based design: model & design (canvas, parametric CAD), simulate & test (notebook, visualisation, optimisation & RL), and generate (Rust code generation, generative design), plus interop and collaboration across the whole loop."
        />
      </Head>

      {/* ── Hero ────────────────────────────────────────────────────────── */}
      <HeroBackdrop image={productHero}>
        <Eyebrow>{PRODUCT_HERO.eyebrow}</Eyebrow>
        <h1 className="mt-5 text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl">
          {PRODUCT_HERO.title}
        </h1>
        <p className="mt-5 text-xl leading-relaxed text-muted-foreground">
          {PRODUCT_HERO.intro}
        </p>
        <div className="mt-8 flex flex-wrap items-center gap-3">
          <Button to="/book-demo">Book a demo</Button>
          <Button to="/demos" variant="ghost">See the demos</Button>
        </div>
      </HeroBackdrop>

      {/* ── Loop diagram ────────────────────────────────────────────────── */}
      <Section bordered>
        <LoopDiagram />
      </Section>

      {/* ── Stats strip ─────────────────────────────────────────────────── */}
      <StatsStrip />

      {/* ══ GROUP 1 — MODEL & DESIGN ════════════════════════════════════ */}
      <GroupHeader
        eyebrow="① MODEL & DESIGN"
        title="Capture every aspect of the system."
        intro="The canvas and the notebook are where the digital replica takes shape — block-diagram physics and parametric geometry, side by side."
      />

      <PillarSection flip={false}>
        {[
          <TextPane key="text" eyebrow={PILLAR_CANVAS.eyebrow} title={PILLAR_CANVAS.title} body={PILLAR_CANVAS.body}>
            <BulletList items={PILLAR_CANVAS.bullets} />
          </TextPane>,
          <CanvasMock key="visual" />,
        ]}
      </PillarSection>

      <PillarSection flip wash="surface" bordered>
        {[
          <TextPane key="text" eyebrow={PILLAR_3D.eyebrow} title={PILLAR_3D.title} body={PILLAR_3D.body}>
            <BulletList items={PILLAR_3D.bullets} />
          </TextPane>,
          <CadMock key="visual" />,
        ]}
      </PillarSection>

      {/* ══ GROUP 2 — SIMULATE & TEST ═══════════════════════════════════ */}
      <GroupHeader
        eyebrow="② SIMULATE & TEST"
        title="Run the replica. Prove it before hardware."
        intro="A reactive notebook, eight renderer flavours, and a full optimisation/RL studio — all streaming live as the solver integrates."
      />

      <PillarSection flip={false}>
        {[
          <TextPane key="text" eyebrow={PILLAR_NOTEBOOK.eyebrow} title={PILLAR_NOTEBOOK.title} body={PILLAR_NOTEBOOK.body}>
            <BulletList items={PILLAR_NOTEBOOK.bullets} />
          </TextPane>,
          <NotebookMock key="visual" />,
        ]}
      </PillarSection>

      <PillarSection flip wash="surface" bordered>
        {[
          <TextPane key="text" eyebrow={PILLAR_VIZ.eyebrow} title={PILLAR_VIZ.title} body={PILLAR_VIZ.body}>
            <BulletList items={PILLAR_VIZ.bullets} />
          </TextPane>,
          <VizMock key="visual" />,
        ]}
      </PillarSection>

      <PillarSection flip={false} bordered>
        {[
          <TextPane key="text" eyebrow={PILLAR_OPTIM.eyebrow} title={PILLAR_OPTIM.title} body={PILLAR_OPTIM.body}>
            <BulletList items={PILLAR_OPTIM.bullets} />
          </TextPane>,
          <OptimMock key="visual" />,
        ]}
      </PillarSection>

      {/* ══ GROUP 3 — GENERATE ══════════════════════════════════════════ */}
      <GroupHeader
        eyebrow="③ GENERATE"
        title="Turn the validated replica into what ships."
        intro="Deterministic embedded firmware and FMI units today; physical parts via generative design as the direction we're building toward."
      />

      <PillarSection flip wash="surface" bordered>
        {[
          <TextPane key="text" eyebrow={PILLAR_CODEGEN.eyebrow} title={PILLAR_CODEGEN.title} body={PILLAR_CODEGEN.body}>
            <BulletList items={PILLAR_CODEGEN.bullets} />
          </TextPane>,
          <CodegenMock key="visual" />,
        ]}
      </PillarSection>

      <PillarSection flip={false} bordered>
        {[
          <TextPane key="text" eyebrow={PILLAR_GENERATIVE.eyebrow} title={PILLAR_GENERATIVE.title} body={PILLAR_GENERATIVE.body}>
            <BulletList items={PILLAR_GENERATIVE.bullets} />
          </TextPane>,
          <GenerativeMock key="visual" />,
        ]}
      </PillarSection>

      {/* ══ CROSS-CUTTING ═══════════════════════════════════════════════ */}
      <GroupHeader
        eyebrow="ACROSS THE WHOLE LOOP"
        title="Open, collaborative, reproducible."
        intro="Interoperability and team collaboration sit under every pillar — open standards in and out, multi-user editing, and bit-exact provenance on every run."
      />

      <PillarSection flip wash="surface" bordered>
        {[
          <TextPane key="text" eyebrow={PILLAR_COMPAT.eyebrow} title={PILLAR_COMPAT.title} body={PILLAR_COMPAT.body}>
            <BulletList items={PILLAR_COMPAT.bullets} />
          </TextPane>,
          <InteropMock key="visual" />,
        ]}
      </PillarSection>

      <PillarSection flip={false} bordered>
        {[
          <TextPane key="text" eyebrow={PILLAR_COLLAB.eyebrow} title={PILLAR_COLLAB.title} body={PILLAR_COLLAB.body}>
            <BulletList items={PILLAR_COLLAB.bullets} />
          </TextPane>,
          <CollabMock key="visual" />,
        ]}
      </PillarSection>

      {/* ── CTA band ────────────────────────────────────────────────────── */}
      <Section wash="wash">
        <SectionHeading
          eyebrow="GET STARTED"
          title={PRODUCT_CTA.title}
          intro={PRODUCT_CTA.body}
          align="center"
        />
        <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
          <Button to={PRODUCT_CTA.primary.to}>{PRODUCT_CTA.primary.label}</Button>
          <Button to={PRODUCT_CTA.secondary.to} variant="ghost">
            {PRODUCT_CTA.secondary.label}
          </Button>
        </div>
      </Section>
    </>
  );
}
